import React, { useEffect, useState, useMemo } from 'react';
import { Form, Button, InputGroup, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import LoaderOverlay from '../../Components/shared/Loader';
import AlertService from '../../services/utility/alertService';
import { getPendingMasterRequests } from '../../services/api/masterRequestService';
import { getTypeLabel } from './masterRequestConfig';
import { TypeBadge } from './DynamicFields';
import './MasterRequest.css';

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (isNaN(date)) return '-';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Days a request has been waiting - the queue is ordered newest first, so this is
// what tells the admin which ones have been sitting.
const ageInDays = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date)) return null;
    return Math.floor((Date.now() - date.getTime()) / 86400000);
};

const AgeBadge = ({ reqDate }) => {
    const days = ageInDays(reqDate);
    if (days === null) return null;

    const variant = days >= 7 ? 'danger' : days >= 3 ? 'warning' : 'secondary';
    const label = days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`;

    return <Badge bg={variant}>{label}</Badge>;
};

function MasterRequestApprovalListPage() {
    const [requests, setRequests] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const handleView = (reqId) => navigate(`/MasterRequestApproverDetail/${reqId}`);

    useEffect(() => {
        const fetchRequests = async () => {
            try {
                const response = await getPendingMasterRequests();
                setRequests(response.data || []);
            } catch (error) {
                console.error('Error fetching pending requests:', error);
                if (error.response?.status === 403) {
                    AlertService.errorAlert('You do not have permission to review Customer/Item requests.');
                } else {
                    AlertService.errorAlert('Unable to load the review queue. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchRequests();
    }, []);

    const filteredData = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return requests;

        return requests.filter((item) => [
            String(item.ID),
            item.REQ_NAME,
            getTypeLabel(item.REQ_TYPE),
            item.EMP_NAME,
            item.CUS_CODE,
            item.CUS_NAME,
            formatDate(item.REQ_DATE)
        ].some(field => (field || '').toString().toLowerCase().includes(term)));
    }, [requests, searchTerm]);

    return (
        <div className="card aon-card master-request-page">
            <LoaderOverlay loading={loading} message="Loading" height="500px" />
            <div className="card-header aon-card-header">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h4 className="mb-0">Customer/Item Requests</h4>
                    <Badge bg="warning" className="align-middle">
                        {requests.length} open
                    </Badge>
                </div>
            </div>

            <div className="card-body aon-card-body">
                <InputGroup className="mb-3">
                    <Form.Control
                        placeholder="Search by name, requester or customer..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label="Search requests"
                    />
                    {searchTerm && (
                        <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                            Clear
                        </Button>
                    )}
                </InputGroup>

                {filteredData.length === 0 ? (
                    <div className="text-center text-muted py-5">
                        {requests.length === 0
                            ? 'No requests are waiting for review.'
                            : 'No requests match your search.'}
                    </div>
                ) : (
                    <>
                        <div className="d-md-none">
                            {filteredData.map((item) => (
                                <div
                                    className="mrq-card mrq-card-link"
                                    key={item.ID}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleView(item.ID)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleView(item.ID); }}
                                >
                                    <div className="d-flex justify-content-between align-items-start gap-2">
                                        <div className="mrq-card-title">{item.REQ_NAME}</div>
                                        <AgeBadge reqDate={item.REQ_DATE} />
                                    </div>
                                    <div className="mrq-card-meta">
                                        #{item.ID} &middot; <TypeBadge reqType={item.REQ_TYPE} /> &middot; {formatDate(item.REQ_DATE)}
                                    </div>
                                    <div className="mrq-card-meta">Raised by: {item.EMP_NAME || '-'}</div>
                                    {item.CUS_NAME && (
                                        <div className="mrq-card-meta">Customer: {item.CUS_NAME}</div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="d-none d-md-block sf-bs-data-table">
                            <div className="table-responsive">
                                <table
                                    className="table table-striped table-bordered aon-booking-table"
                                    style={{ width: '100%' }}
                                >
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th>Name</th>
                                            <th>Raised By</th>
                                            <th>Customer</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((item) => (
                                            <tr key={item.ID}>
                                                <td>{item.ID}</td>
                                                <td>{formatDate(item.REQ_DATE)}</td>
                                                <td><TypeBadge reqType={item.REQ_TYPE} /></td>
                                                <td>{item.REQ_NAME}</td>
                                                <td>{item.EMP_NAME || '-'}</td>
                                                <td>{item.CUS_NAME || '-'}</td>
                                                <td>
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        onClick={() => handleView(item.ID)}
                                                    >
                                                        Review
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default MasterRequestApprovalListPage;
